<template>
	<div
		ref="bookerRef"
		class="global-booker__container"
		:class="{
			'global-booker__nav-container': nav,
			'global-booker__nav-container--mobile': accordion,
			'global-booker__container--with-end-location': (nav && accordion) || !nav,
		}">
		<div class="global-booker__field global-booker__start-location">
			<v-btn flat class="global-booker__field-button global-booker__field-button--place" @click="openLocationMenu(EBookerLocationType.START)">
				<GlobalIcon :name="startLocationIcon" :height="20" class="global-booker__field-icon"></GlobalIcon>
				<Transition name="fade" mode="out-in">
					<div v-if="!startLocation" class="global-booker__field-content">
						<div class="global-booker__field-label">{{ $t('components.booker.container.start_location_label') }}</div>
						<div class="global-booker__field-value">{{ $t('components.booker.container.start_location_placeholder') }}</div>
					</div>
					<div v-else class="global-booker__field-content">
						<Transition name="fade" mode="out-in">
							<template v-if="startLocation.isDelivery">
								<div v-if="!otherEndLocation" class="global-booker__field-label">
									{{ $t('components.booker.container.substitution_and_pickup_address') }}
								</div>
								<div v-else class="global-booker__field-label">{{ $t('components.booker.container.car_substitution_address') }}</div>
							</template>
							<template v-else>
								<div v-if="!otherEndLocation" class="global-booker__field-label">
									{{ $t('components.booker.container.pickup_dropoff_location_label') }}
								</div>
								<div v-else class="global-booker__field-label">
									{{ $t('components.booker.container.start_location_placeholder') }}
								</div>
							</template>
						</Transition>
						<div class="global-booker__field-value">
							<!-- <span v-if="startLocation.city">{{ startLocation.city }},</span> -->
							{{ title(startLocation) }}
						</div>
					</div>
				</Transition>
			</v-btn>
			<ClientOnly>
				<LazyGlobalBookerLocationMenu
					v-if="hasStartLocationMenuBeenOpened"
					v-model="isStartLocationMenuOpen"
					:nav
					:type="EBookerLocationType.START" />
			</ClientOnly>
		</div>

		<Transition name="fade" mode="out-in">
			<Skeletor
				v-if="!isHydrated"
				class="global-booker__skeletor"
				:class="{ 'global-booker__skeletor--nav': nav, 'global-booker__skeletor--accordion': nav && accordion }" />
		</Transition>

		<Transition name="fade" mode="out-in">
			<div v-if="!otherEndLocation" class="global-booker__field global-booker__end-location">
				<GlobalCheckbox v-model="otherEndLocation">{{ $t('components.booker.container.other_end_location_checkbox') }}</GlobalCheckbox>
			</div>
			<div v-else ref="endRef" class="global-booker__field">
				<v-btn flat class="global-booker__field-button" @click="openLocationMenu(EBookerLocationType.END)">
					<GlobalIcon :name="endLocationIcon" :height="20" class="global-booker__field-icon"></GlobalIcon>
					<Transition name="fade" mode="out-in">
						<div v-if="endLocation?.isDelivery" class="global-booker__field-content">
							<div class="global-booker__field-label">{{ $t('components.booker.container.return_location_address') }}</div>
							<div class="global-booker__field-value global-booker__field-value--end">
								<!-- <span v-if="endLocation.city">{{ endLocation.city }},</span> -->
								{{ title(endLocation) }}
							</div>
						</div>
						<template v-else>
							<Transition name="fade" mode="out-in">
								<div v-if="!endLocation" class="global-booker__field-content">
									<div class="global-booker__field-label">{{ $t('components.booker.container.end_location_label') }}</div>
									<div class="global-booker__field-value">{{ $t('components.booker.container.end_location_placeholder') }}</div>
								</div>
								<div v-else class="global-booker__field-content">
									<div class="global-booker__field-label">{{ $t('components.booker.container.end_location_placeholder') }}</div>
									<div class="global-booker__field-value global-booker__field-value--end">
										{{ title(endLocation) }}
									</div>
								</div>
							</Transition>
						</template>
					</Transition>
					<ButtonIcon class="global-booker__field-button-cancel" @click="cancelLocation()">
						<GlobalIcon name="close" :height="23" />
					</ButtonIcon>
				</v-btn>
				<ClientOnly>
					<LazyGlobalBookerLocationMenu
						v-if="hasEndLocationMenuBeenOpened"
						v-model="isEndLocationMenuOpen"
						:nav
						:type="EBookerLocationType.END" />
				</ClientOnly>
			</div>
		</Transition>

		<div class="global-booker__field global-booker__start-date">
			<v-btn flat class="global-booker__field-button" @click="openDateMenu">
				<GlobalIcon name="calendar" :height="20" class="global-booker__field-icon"></GlobalIcon>
				<div v-if="dates.start" class="global-booker__field-content">
					<div class="global-booker__field-label">{{ $t('components.booker.container.pickup_date_label') }}</div>
					<div class="global-booker__field-value">
						{{ format(dates.start, 'dd MMM yyyy, HH:mm', { locale: bookerLocale }) }}
					</div>
				</div>
			</v-btn>
			<ClientOnly>
				<LazyGlobalBookerDateMenu v-if="hasDateMenuBeenOpened" v-model="isStartDateMenuOpen" :nav />
			</ClientOnly>
		</div>
		<div class="global-booker__field global-booker__end-date">
			<v-btn flat class="global-booker__field-button" @click="openDateMenu">
				<GlobalIcon name="calendar" :height="20" class="global-booker__field-icon"></GlobalIcon>

				<div v-if="dates.end" class="global-booker__field-content">
					<div class="global-booker__field-label">{{ $t('components.booker.container.dropoff_date_label') }}</div>
					<div class="global-booker__field-value">
						{{ format(dates.end, 'dd MMM yyyy, HH:mm', { locale: bookerLocale }) }}
					</div>
				</div>
			</v-btn>
		</div>
		<div class="global-booker__button" :class="{ 'global-booker__nav-button': nav }">
			<ButtonPrimary iconBefore="search-normal" class="global-booker__button-search" :disabled="isButtonDisabled" @click="searchCars"
				>{{ $t('components.booker.container.search_button') }}
			</ButtonPrimary>
		</div>
		<GlobalBookerOpinion
			v-if="!nav && location && locationRatingsForm.url && locationRatingsForm.ratingsCount && locationRatingsForm.ratings"
			v-show="isTablet_sm"
			class="global-booker__google"
			:googleUrl="locationRatingsForm.url"
			:countRatings="Number(locationRatingsForm.ratingsCount)"
			:ratings="locationRatingsForm.ratings"
			main />
		<GlobalBookerOpinion v-else-if="!nav" v-show="isTablet_sm" class="global-booker__google" main />
		<GlobalBookerTrustpilotOpinion v-if="!nav" v-show="isTablet_sm" class="global-booker__google" main />
		<div
			v-if="(nav && accordion) || !nav"
			class="global-booker__end-location--mobile"
			:class="{ 'global-booker__end-location--accordion': nav && accordion }">
			<GlobalCheckbox v-model="otherEndLocation">{{ $t('components.booker.container.other_end_location_checkbox') }}</GlobalCheckbox>
		</div>
	</div>
</template>

<script lang="ts" setup>
import { Skeletor } from 'vue-skeletor'
import { format } from 'date-fns'
import { pl, enGB } from 'date-fns/locale'
import { EBookerLocationType, ELocationPurpose } from '@/data/bookerData'
import { useUrlFilters } from '@/modules/carsModule/composables/useUrlFilters'

const bookerRef = ref<HTMLElement | null>(null)
const bookerWidth = ref(0)
const endRef = ref<HTMLElement | null>(null)
const locationModalOffset = ref(0)
const router = useRouter()
const { startLocation, endLocation, dates, otherEndLocation, isStartLocationMenuOpen, isEndLocationMenuOpen, isStartDateMenuOpen } = useBookerForm()
const state = inject<Ref<Record<string, boolean>>>('state')!
const isCarsPage = inject<ComputedRef<boolean>>(
	'isCarsPage',
	computed(() => false),
)
const urlFilters = useUrlFilters()
const localePath = useLocalePath()
const { locale } = useI18n()
const googleRatingsStore = useGoogleRatingsStore()
const trustpilotRatingsStore = useTrustpilotRatingsStore()
const bookingStore = useBookingStore()
const mapStore = useMapStore()
const isHydrated = ref(false)

const isButtonDisabled = ref(true)

watch(
	() => startLocation.value,
	() => {
		if (startLocation.value) {
			isButtonDisabled.value = false
		} else {
			isButtonDisabled.value = true
		}
	},
	{ immediate: true, deep: true },
)

onMounted(async () => {
	isHydrated.value = true
	isButtonDisabled.value = true
	await nextTick()
	isButtonDisabled.value = startLocation.value === null
})

const props = defineProps<{
	nav?: boolean
	accordion?: boolean
	location?: boolean
}>()

const ratingsForm = reactive({
	stars: '',
	ratings: '',
	ratingsCount: '',
	url: '',
})

const locationRatingsForm = reactive({
	stars: '',
	ratings: '',
	ratingsCount: '',
	url: '',
})

if (props.location) {
	const assignedLocations = inject<Ref<ILocationModel[]>>('assignedLocations')!
	const primaryLocation = assignedLocations?.value?.[0]

	if (primaryLocation?.ratings && primaryLocation?.countRatings && primaryLocation?.googleUrl) {
		locationRatingsForm.stars = primaryLocation.ratings
		locationRatingsForm.ratings = primaryLocation.ratings
		locationRatingsForm.ratingsCount = primaryLocation.countRatings
		locationRatingsForm.url = primaryLocation.googleUrl
	} else if (!props.nav) {
		// branch has no Google reviews of its own - fall back to the company-wide reviews (same as the home page)
		await googleRatingsStore.fetchGoogleRating()
	}
} else if (!props.nav) {
	await Promise.all([googleRatingsStore.fetchGoogleRating(), trustpilotRatingsStore.fetchTrustpilotRating()])
}

watch(
	() => googleRatingsStore.getGoogleRating,
	(v) => {
		if (v) {
			ratingsForm.stars = v.stars
			ratingsForm.ratings = v.ratings
			ratingsForm.ratingsCount = v.ratingsCount
			ratingsForm.url = v.url
		}
	},
	{ immediate: true },
)

provide('ratingsForm', ratingsForm)

const cancelLocation = () => {
	otherEndLocation.value = false
	if (isCarsPage.value) return
	bookingStore.setBookerLocations({ start: startLocation.value, end: null })
	bookingStore.updateBooking()
}

const bookerLocale = computed(() => {
	switch (locale.value) {
		case 'pl':
			return pl
		case 'en':
			return enGB
		default:
			return enGB
	}
})

provide('bookerWidth', bookerWidth)
provide('locationModalOffset', locationModalOffset)

const startLocationIcon = computed(() => {
	const start = startLocation.value
	return start && start.icon ? start.icon : start?.isDelivery ? 'routing' : 'location'
})

const endLocationIcon = computed(() => {
	const end = endLocation.value
	return end && end.icon ? end.icon : end?.isDelivery ? 'routing' : 'location'
})

const bookerObserver = ref<ResizeObserver | null>(null)

const updateEndRefOffset = () => {
	if (endRef.value) {
		requestAnimationFrame(() => {
			if (endRef.value) locationModalOffset.value = endRef.value.getBoundingClientRect().left
		})
	}
}

onMounted(() => {
	if (bookerRef.value) {
		bookerObserver.value = new ResizeObserver(() => {
			if (bookerRef.value) {
				bookerWidth.value = (window.innerWidth - bookerRef.value.clientWidth) / 2
			}
		})
		bookerObserver.value.observe(bookerRef.value)
	}

	if (isCarsPage.value && mapStore.getClickedLocation) {
		startLocation.value = mapStore.getClickedLocation
		mapStore.setClickedLocation(null)
	}

	updateEndRefOffset()
})

onBeforeUnmount(() => {
	bookerObserver.value?.disconnect()
})

watch(
	() => endRef.value,
	() => updateEndRefOffset(),
)

const { isTablet_sm } = useWindowWidth()

const hasStartLocationMenuBeenOpened = ref(false)
const hasEndLocationMenuBeenOpened = ref(false)

const openLocationMenu = (type: EBookerLocationType) => {
	if (type === EBookerLocationType.START) {
		if (!hasStartLocationMenuBeenOpened.value) hasStartLocationMenuBeenOpened.value = true
		isStartLocationMenuOpen.value = true
	} else {
		if (!hasEndLocationMenuBeenOpened.value) hasEndLocationMenuBeenOpened.value = true
		isEndLocationMenuOpen.value = true
	}
	history.pushState({ menu: 'location' }, '')
}

const hasDateMenuBeenOpened = ref(false)

const openDateMenu = () => {
	if (!hasDateMenuBeenOpened.value) hasDateMenuBeenOpened.value = true
	isStartDateMenuOpen.value = true
	history.pushState({ menu: 'date' }, '')
}

watch(
	isStartLocationMenuOpen,
	(open: boolean) => {
		if (open) hasStartLocationMenuBeenOpened.value = true
	},
	{ immediate: true },
)

watch(
	isStartDateMenuOpen,
	(open: boolean) => {
		if (open) hasDateMenuBeenOpened.value = true
	},
	{ immediate: true },
)

const searchCars = () => {
	const cookie = useCookie('cf-car')
	if (cookie.value) {
		bookingStore.updateSelectedCar(cookie.value)
		router.push(localePath({ name: 'reservation', query: { step: '1' } }))
		cookie.value = null
	} else {
		const location = startLocation.value
		const dateOut = dates.value.start ? format(dates.value.start, "yyyy-MM-dd'T'HH:mm") : null
		const dateIn = dates.value.end ? format(dates.value.end, "yyyy-MM-dd'T'HH:mm") : null
		const officeId = location?.crmID ? String(location.crmID) : null

		if (isCarsPage.value) {
			urlFilters.setDateAndLocation(dateOut, dateIn, officeId)
		} else {
			const query: Record<string, string> = {}
			if (dateOut) query.date_out = dateOut
			if (dateIn) query.date_in = dateIn
			if (officeId) query.office_id = officeId
			router.push(localePath({ name: 'cars', query }))
		}
	}
	state.value.booker = false
	sendAnalyticsData()
}

const i18nService = usei18nService()

const title = (location: any) => {
	if (!location.city) return location.name
	else if (location.purpose === ELocationPurpose.MEET_AND_GREET)
		return `${i18nService.$tDynamic('data.cities', location.city)}, ${locale.value === 'pl' ? location.name : location.nameEn}`
	else if ((location.city && location.icon === 'airplane') || (location.city && location.icon === 'train')) {
		return `${locale.value === 'pl' ? location.name : location.nameEn}`
	} else {
		return `${i18nService.$tDynamic('data.cities', location.city)}, ${location.addressStreet} ${location.addressHouseNumber ?? ''}`
	}
}

const sendAnalyticsData = () => {
	const gtmService = useGTMService()
	if (!gtmService) return
	gtmService.searchButtonClicked({})
}
</script>

<style lang="scss">
@use '@/assets/scss/components/accordion';
@import 'vue-skeletor/dist/vue-skeletor.css';
</style>
<style lang="scss" src="./Container.scss"></style>
